'use client'

import { cssVariablesResolver, theme } from '@/theme'
import { MantineProvider } from '@mantine/core'
import { ModalsProvider } from '@mantine/modals'
import { type FC, type ReactNode } from 'react'
// QueryClientProvider relies on useContext, hence 'use client':
// https://tanstack.com/query/latest/docs/framework/react/guides/advanced-ssr
import { ErrorBoundary } from '@/components/error-boundary'
import { SpyModeProvider } from '@/components/spy-mode-context'
import { YjsWebsocketProvider } from '@/lib/realtime/yjs-websocket-context'
import { SessionInfoProvider } from '@/components/layout/session-info-context'
// eslint-disable-next-line no-restricted-imports
import { isServer, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { usePostHogInit } from '@/hooks/use-posthog-init'
import { reportQueryError } from '@/hooks/query-wrappers'
import { ToastProvider } from './toast-provider'

function makeQueryClient() {
    return new QueryClient({
        // Queries carry no error handling of their own, so before this a failed poll ended in the
        // cache and reached nobody. A stale action id always reports, because a poll meeting one is
        // the only measure of how often that happens; everything else is opt-in through `meta`, so a
        // page of background polls cannot stack one toast per query (OTTER-726).
        queryCache: new QueryCache({
            onError: reportQueryError,
        }),
        defaultOptions: {
            queries: {
                // A staleTime above 0 avoids re-fetching immediately on the client under SSR.
                staleTime: 60 * 1000,
                refetchInterval: 15 * 1000 * 60,
            },
        },
    })
}
let browserQueryClient: QueryClient | undefined = undefined
type Props = {
    children: ReactNode
    singleUserEditing?: boolean
    posthogProjectToken?: string
    userInfo?: UserInfo | null
    userInfoUpdatedAt?: number
}
export function getQueryClient() {
    if (isServer) {
        return makeQueryClient()
    } else {
        // Reused so React suspending during the initial render does not remake the client.
        if (!browserQueryClient) browserQueryClient = makeQueryClient()
        return browserQueryClient
    }
}

export const Providers: FC<Props> = ({
    children,
    singleUserEditing = false,
    posthogProjectToken = '',
    userInfo = null,
    userInfoUpdatedAt,
}) => {
    const queryClient = getQueryClient()
    usePostHogInit(posthogProjectToken)

    return (
        <QueryClientProvider client={queryClient}>
            <SessionInfoProvider userInfo={userInfo} updatedAt={userInfoUpdatedAt}>
                <MantineProvider theme={theme} cssVariablesResolver={cssVariablesResolver}>
                    <ToastProvider />
                    <ModalsProvider>
                        <ErrorBoundary>
                            <SpyModeProvider>
                                <YjsWebsocketProvider singleUserEditing={singleUserEditing}>
                                    {children}
                                </YjsWebsocketProvider>
                            </SpyModeProvider>
                        </ErrorBoundary>
                    </ModalsProvider>
                </MantineProvider>
            </SessionInfoProvider>
        </QueryClientProvider>
    )
}
